/**
 * TodoSubtaskRow - Subtask row with square checkbox matching sticky note styling.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Pressable, StyleSheet, Text, TextInput, View } from 'react-native';
import * as Haptics from 'expo-haptics';
import { Ionicons } from '@expo/vector-icons';

import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { useTodoScroll } from './TodoScrollContext';
import type { Subtask } from '@/types/tasks';

type TodoSubtaskRowProps = {
  subtask: Subtask;
  palette: typeof Colors.light;
  onToggle: (subtaskId: string) => void;
  onUpdateTitle?: (subtaskId: string, newTitle: string) => void;
  onDelete?: (subtaskId: string) => void;
  onEditingChange?: (isEditing: boolean) => void;
  /** Whether this subtask row is focused (shows delete button) */
  isFocused?: boolean;
  onFocus?: (subtaskId: string) => void;
  textColor?: string;
  checkboxColor?: string;
  /** Auto-enter edit mode when this becomes true (for chained new rows) */
  forceEditing?: boolean;
  /** Called once after this row has consumed forceEditing, so the parent can clear it */
  onForceEditingHandled?: () => void;
  /** When set, pressing Enter on a non-empty edit chains a new blank subtask below */
  onSubmitChain?: () => void;
  /** Lets the parent track this row's TextInput so it can focus chained rows imperatively */
  onRegisterInput?: (subtaskId: string, ref: TextInput | null) => void;
};

// Default colors for light backgrounds (pastels)
const DEFAULT_TEXT_COLOR = '#333333';
const DEFAULT_CHECKBOX_COLOR = '#666666';

export function TodoSubtaskRow({
  subtask,
  onToggle,
  onUpdateTitle,
  onDelete,
  onEditingChange,
  isFocused = false,
  onFocus,
  textColor = DEFAULT_TEXT_COLOR,
  checkboxColor = DEFAULT_CHECKBOX_COLOR,
  forceEditing = false,
  onForceEditingHandled,
  onSubmitChain,
  onRegisterInput,
}: TodoSubtaskRowProps) {
  // Mount in edit mode immediately when this row was created via chain (forceEditing=true).
  // This way the chained TextInput is rendered on the first commit, before the previous
  // row's TextInput unmounts — so the keyboard never has a chance to dismiss.
  const [isEditing, setIsEditing] = useState(() => forceEditing);
  const [editValue, setEditValue] = useState(subtask.title);
  const rowRef = useRef<View>(null);
  const inputRef = useRef<TextInput>(null);
  // Set when this row triggered a chain submit. The eventual onBlur (fired when the new
  // row steals focus) uses this to close us out cleanly.
  const chainSubmittingRef = useRef(false);
  const { scrollToInput, setIsEditing: setContextEditing } = useTodoScroll();

  // A row created via chain starts blank — used to auto-clean up if the user types nothing.
  const isBlankNewRow = subtask.title === '' && !subtask.completed;

  const updateEditing = useCallback((editing: boolean) => {
    setIsEditing(editing);
    setContextEditing(editing);
    onEditingChange?.(editing);
  }, [onEditingChange, setContextEditing]);

  useEffect(() => {
    if (forceEditing) {
      setEditValue(subtask.title);
      // Idempotent if already editing (lazy init handled it on mount); still
      // syncs context + parent edit-count and triggers the scroll.
      updateEditing(true);
      // Explicitly steal focus. autoFocus alone doesn't reliably grab focus from
      // another input, and on chains beyond the first one the timing is tighter,
      // so we focus on the next animation frame (post-layout) and again after a
      // short delay as a fallback.
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
      setTimeout(() => {
        inputRef.current?.focus();
        scrollToInput(rowRef);
      }, 60);
      onForceEditingHandled?.();
    }
  }, [forceEditing, subtask.title, updateEditing, scrollToInput, onForceEditingHandled]);

  const handleToggle = useCallback(() => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    onToggle(subtask.id);
  }, [onToggle, subtask.id]);

  const handleRowPress = useCallback(() => {
    onFocus?.(subtask.id);
  }, [onFocus, subtask.id]);

  const handleTitlePress = useCallback(() => {
    if (onUpdateTitle && !subtask.completed) {
      setEditValue(subtask.title);
      updateEditing(true);
      setTimeout(() => scrollToInput(rowRef), 50);
    }
  }, [onUpdateTitle, subtask.completed, subtask.title, updateEditing, scrollToInput]);

  const handleSubmit = useCallback(() => {
    const trimmed = editValue.trim();

    if (trimmed) {
      if (trimmed !== subtask.title) {
        onUpdateTitle?.(subtask.id, trimmed);
      }
      if (onSubmitChain) {
        // Don't blur — the new row will steal focus via autoFocus + explicit
        // focus() calls, which keeps the keyboard up without flicker. Blurring
        // first dismisses the keyboard momentarily and the re-focus race is
        // unreliable for rows inserted in the middle of the list.
        // chainSubmittingRef tells our handleBlur (fired when focus moves) to
        // close us out cleanly rather than re-running save logic.
        chainSubmittingRef.current = true;
        onSubmitChain();
        return;
      }
      updateEditing(false);
      return;
    }

    // Empty submit on a blank chain row → discard the row entirely
    if (isBlankNewRow && onDelete) {
      onDelete(subtask.id);
      updateEditing(false);
      return;
    }

    updateEditing(false);
  }, [editValue, onUpdateTitle, subtask.id, subtask.title, updateEditing, onSubmitChain, isBlankNewRow, onDelete]);

  const handleBlur = useCallback(() => {
    if (chainSubmittingRef.current) {
      chainSubmittingRef.current = false;
      updateEditing(false);
      return;
    }
    const trimmed = editValue.trim();
    if (trimmed) {
      if (trimmed !== subtask.title) {
        onUpdateTitle?.(subtask.id, trimmed);
      }
      updateEditing(false);
      return;
    }
    if (isBlankNewRow && onDelete) {
      onDelete(subtask.id);
      updateEditing(false);
      return;
    }
    updateEditing(false);
  }, [editValue, onUpdateTitle, subtask.id, subtask.title, updateEditing, isBlankNewRow, onDelete]);

  const handleDelete = useCallback(() => {
    Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Light);
    Alert.alert(
      'Delete Subtask',
      `Delete "${subtask.title}"?`,
      [
        { text: 'Cancel', style: 'cancel' },
        { text: 'Delete', style: 'destructive', onPress: () => onDelete?.(subtask.id) },
      ],
    );
  }, [onDelete, subtask.id, subtask.title]);

  return (
    <Pressable ref={rowRef} onPress={handleRowPress} style={styles.container}>
      <Pressable onPress={handleToggle} style={styles.checkboxTouchable} hitSlop={4}>
        <View
          style={[
            styles.checkbox,
            { borderColor: checkboxColor },
            subtask.completed && [styles.checkboxCompleted, { backgroundColor: checkboxColor }],
          ]}
        >
          {subtask.completed && (
            <Ionicons name="checkmark" size={10} color={textColor === '#FFFFFF' ? '#1A1726' : '#FFFFFF'} />
          )}
        </View>
      </Pressable>

      {isEditing ? (
        <TextInput
          ref={(node) => {
            inputRef.current = node;
            onRegisterInput?.(subtask.id, node);
          }}
          style={[styles.titleInput, { color: textColor }]}
          value={editValue}
          onChangeText={setEditValue}
          onSubmitEditing={handleSubmit}
          onBlur={handleBlur}
          autoFocus
          returnKeyType="done"
          blurOnSubmit={false}
        />
      ) : (
        <Pressable onPress={handleTitlePress} style={styles.titleTouchable}>
          <Text
            style={[
              styles.title,
              { color: textColor },
              subtask.completed && styles.completedTitle,
            ]}
            numberOfLines={1}
          >
            {subtask.title}
          </Text>
        </Pressable>
      )}

      {onDelete && (
        <Pressable onPress={handleDelete} style={styles.deleteButton} hitSlop={6}>
          <Ionicons name="close" size={14} color={checkboxColor} style={{ opacity: 0.4 }} />
        </Pressable>
      )}
    </Pressable>
  );
}

// Center 16px subtask checkbox under 24px main checkbox: (24-16)/2 = 4px offset
const CHECKBOX_ALIGNMENT_OFFSET = 4;

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 3,
    paddingLeft: CHECKBOX_ALIGNMENT_OFFSET,
    gap: Spacing.md + 2, // 14px
  },
  checkboxTouchable: {
    alignSelf: 'center',
  },
  checkbox: {
    width: 16,
    height: 16,
    borderRadius: BorderRadius.xs,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxCompleted: {
    // backgroundColor applied dynamically
  },
  titleTouchable: {
    flex: 1,
    justifyContent: 'center',
  },
  title: {
    fontSize: 13,
    lineHeight: 19,
    // color applied dynamically
  },
  titleInput: {
    flex: 1,
    fontSize: 13,
    lineHeight: 19,
    // color applied dynamically
    padding: 0,
  },
  completedTitle: {
    textDecorationLine: 'line-through',
    opacity: 0.5,
  },
  deleteButton: {
    marginLeft: Spacing.xs,
    padding: 2,
  },
});
